'use client';

import { useSearchParams } from 'next/navigation';
import { mockFaq } from '@wwe-portal/web/providers/queries/subscription';
import { FaqList } from '@wwe-portal/ui/features/subscription';
import { useElementFocusBySelector } from '@wwe-portal/web/hooks/persistent/useElementFocus';
import { FaqLayout } from '@wwe-portal/ui/features/subscription/faq/layout';
import { ClickableDiv } from '@wwe-portal/ui/components/button';
import { useState } from 'react';

const FAQ_ITEM_SEARCH_PARAMS = 'faq-item';

interface UseFaqStoreProps {
  defaultExpandedFaqItemId: string | undefined;
  faqList: {
    answer: React.ReactNode;
    question: React.ReactNode;
    id: string;
  }[];
}

export const useFaqStore = ({
  faqList,
  defaultExpandedFaqItemId,
}: UseFaqStoreProps) => {
  const [expandedItems, setExpandedItems] = useState(() => {
    const map = new Map<string, boolean>();
    if (defaultExpandedFaqItemId) map.set(defaultExpandedFaqItemId, true);
    return map;
  });
  const toggle = (itemId: string) => {
    const expanded = Boolean(expandedItems.get(itemId));
    const nextMap = new Map(expandedItems);
    nextMap.set(itemId, !expanded);
    setExpandedItems(nextMap);
  };

  const viewAll = () => {
    const faqIds = faqList.map((faq) => faq.id);
    const nextMap = new Map<string, boolean>();
    for (const id of faqIds) {
      nextMap.set(id, true);
    }
    setExpandedItems(nextMap);
  };

  const data = faqList.map((faqItem) => ({
    ...faqItem,
    expanded: Boolean(expandedItems.get(faqItem.id)),
  }));

  return {
    data,
    defaultExpandedFaqItemId,
    toggle,
    setExpandedItems,
    viewAll,
  };
};

export const FaqSection = () => {
  const search = useSearchParams();
  const params = new URLSearchParams(search);
  const focusFaqItem = params.get(FAQ_ITEM_SEARCH_PARAMS);
  const { data, toggle, setExpandedItems, viewAll } = useFaqStore({
    defaultExpandedFaqItemId: focusFaqItem ?? undefined,
    faqList: mockFaq.faqList,
  });

  useElementFocusBySelector({
    selector: `[data-faq-id="${focusFaqItem}"]`,
    enabled: Boolean(focusFaqItem),
    onScrolled: () => {
      const nextParams = new URLSearchParams(search);
      nextParams.delete(FAQ_ITEM_SEARCH_PARAMS);
      window.history.replaceState({}, '', `?${nextParams.toString()}`);
      setExpandedItems((value) => {
        if (
          focusFaqItem &&
          !value?.get(focusFaqItem) &&
          data.some(({ id }) => id === focusFaqItem)
        ) {
          const nextMap = new Map(value);
          nextMap.set(focusFaqItem, true);
          return nextMap;
        }
        return value;
      });
    },
  });

  return (
    <FaqLayout
      head={
        <div className="flex flex-row items-baseline justify-between">
          <h1 className="text-text300 text-base font-semibold">
            Top Questions
          </h1>
          <ClickableDiv
            className="text-green800 cursor-pointer font-[12px]"
            onClick={viewAll}
          >
            View all
          </ClickableDiv>
        </div>
      }
    >
      <FaqList data={data} onToggle={toggle} />
    </FaqLayout>
  );
};
